import React, { useEffect, useMemo, useState } from "react";
import dbPrefixes from "../../config";
import {
  Box,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Fade,
  Fab,
  TextField,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  useMaterialReactTable,
  MaterialReactTable,
} from "material-react-table";
import { useTheme } from "@emotion/react";
import { CustomThemeType } from "../../context/Providers";
import DashboardLayout from "../../layouts/DashboardLayout";
import UserIcon from "@mui/icons-material/LocalShipping";
import type { Truck } from "../../types/DB2";
import { useUserData } from "../../context/UserDataFetcher";
import { useTrucksData } from "../../context/TrucksDataProvider";

type FormTruck = Partial<Truck> & { driversCsv?: string };

const TruckModal: React.FC<{
  open: boolean;
  onClose: () => void;
  onSave: (payload: FormTruck) => Promise<void> | void;
  truck?: Truck | null;
  bulk?: boolean;
}> = ({ open, onClose, onSave, truck, bulk }) => {
  const isEdit = !!truck && !bulk;
  const [form, setForm] = useState<FormTruck>({});
  React.useEffect(() => {
    if (truck) {
      setForm({
        ...truck,
        driversCsv: truck.drivers ? (truck.drivers as string[]).join(", ") : "",
      });
    } else {
      setForm({});
    }
  }, [truck, open, bulk]);

  const handleChange = (k: keyof FormTruck, v: any) =>
    setForm((s) => ({ ...s, [k]: v }));

  const handleSubmit = async () => {
    const out: FormTruck = { ...form };
    if (out.driversCsv !== undefined) {
      out.drivers = out.driversCsv
        ? out.driversCsv
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean)
        : [];
      delete out.driversCsv;
    }
    // prune empty
    Object.keys(out).forEach((k) => {
      const v = (out as any)[k];
      if (v === "" || v === undefined || v === null) delete (out as any)[k];
    });
    await onSave(out);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {bulk ? "Edit Selected Trucks" : isEdit ? "Edit Truck" : "Add Truck"}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          {!bulk && (
            <TextField
              label="License Plate"
              value={form.licensePlate ?? ""}
              onChange={(e) => handleChange("licensePlate", e.target.value)}
              fullWidth
              required={!bulk && !isEdit}
            />
          )}
          <TextField
            label="Truck Company (optional)"
            value={form.truckCompanyId ?? ""}
            onChange={(e) => handleChange("truckCompanyId", e.target.value)}
            fullWidth
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained">
          {bulk ? "Apply to Selected" : isEdit ? "Save" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const ManageTrucks: React.FC = () => {
  const userData = useUserData();
  const theme: CustomThemeType = useTheme() as CustomThemeType;

  const { trucks, refresh, createTruck, updateTruck, deleteTruck } = useTrucksData();

  const [modalOpen, setModalOpen] = useState(false);
  const [editingTruck, setEditingTruck] = useState<Truck | null>(null);
  const [isBulkEdit, setIsBulkEdit] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [menuTruck, setMenuTruck] = useState<Truck | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deletingTruck, setDeletingTruck] = useState<Truck | null>(null);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);

  const columns = useMemo(
    () => [
      {
        accessorKey: "licensePlate",
        header: "License Plate",
        muiTableHeadCellProps: { sx: { width: "33%", px: 1 } },
        muiTableBodyCellProps: { sx: { width: "33%", px: 1 } },
      },
      {
        accessorKey: "truckCompanyId",
        header: "Truck Company",
        Cell: ({ row }: any) => {
          const t = row.original as Truck;
          // prefer explicit truckCompanyId, fall back to user's manufacturerId or dealerId
          const raw = (t.truckCompanyId ?? "").toString().trim();
          const fallback = userData?.manufacturerId ?? userData?.dealerId ?? "";
          const display = raw.length > 0 ? raw : fallback;
          return <>{display}</>;
        },
        muiTableHeadCellProps: { sx: { width: "34%", px: 1 } },
        muiTableBodyCellProps: { sx: { width: "34%", px: 1 } },
      },
    ],
    // include userData so fallback updates when user context changes
    [userData]
  );

  const table = useMaterialReactTable({
    columns,
    data: trucks ?? [],
    enableColumnActions: false,
    enableColumnFilters: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableHiding: false,
    enableBottomToolbar: true,
    positionToolbarAlertBanner: "none",
    enableRowSelection: true,
    enableMultiRowSelection: true,
    enableRowActions: true,
    positionActionsColumn: "last",
    displayColumnDefOptions: {
      "mrt-row-actions": {
        header: "",
        muiTableHeadCellProps: (theme) => ({
          sx: { width: 56, maxWidth: 56, minWidth: 56, px: 1 },
        }),
        muiTableBodyCellProps: {
          sx: { width: 56, maxWidth: 56, minWidth: 56, textAlign: "right" },
        },
      },
      "mrt-row-select": {
        header: "",
        muiTableHeadCellProps: {
          sx: { width: 128, maxWidth: 128, minWidth: 128, px: 1 },
        },
        muiTableBodyCellProps: {
          sx: { width: 48, maxWidth: 48, minWidth: 48, px: 1 },
        },
      },
    },
    muiTableProps: {
      sx: (t) => ({
        elevation: 0,
        boxShadow: "none",
      }),
    },
    muiTableBodyProps: {
      sx: (theme) => ({
        backgroundColor: theme.palette.background.paper,
      }),
    },
    muiTableHeadRowProps: {
      sx: (t) => ({
        backgroundColor: t.palette.background.paper,
        "& th": {
          borderBottom: `1px solid ${t.palette.divider} !important`,
        },
      }),
    },
    muiTableBodyRowProps: {
      sx: (t) => ({
        backgroundColor: t.palette.background.paper,
        "&:hover": {
          cursor: "pointer",
          backgroundColor: t.palette.action.hover,
        },
        "&.Mui-selected": {
          backgroundColor: t.palette.action.selected,
        },
        "& td": {
          borderBottom: `1px solid ${t.palette.divider} !important`,
        },
        "& td:after": {
          backgroundColor: "transparent !important",
        },
      }),
    },
    muiBottomToolbarProps: {
      sx: (theme) => ({
        backgroundColor: theme.palette.background.paper,
      }),
    },
    renderRowActions: ({ row }) => (
      <IconButton
        size="small"
        edge="end"
        onClick={(e) => {
          setMenuAnchorEl(e.currentTarget);
          setMenuTruck(row.original as Truck);
        }}
        aria-controls={menuAnchorEl ? "row-actions-menu-truck" : undefined}
        aria-haspopup="true"
        aria-expanded={Boolean(menuAnchorEl) ? "true" : undefined}
      >
        <MoreVertIcon />
      </IconButton>
    ),
    muiTopToolbarProps: {
      sx: (t) => ({
        display: "flex",
        alignItems: "center",
        backgroundColor: t.palette.background.paper,
        "& > div": {
          flexDirection: "row-reverse",
        },
      }),
    },
    renderTopToolbarCustomActions: ({ table: _table }) => (
      <IconButton
        color="primary"
        onClick={() => {
          setEditingTruck(null);
          setIsBulkEdit(false);
          setModalOpen(true);
        }}
      >
        <AddIcon />
      </IconButton>
    ),
    initialState: { density: "spacious", pagination: { pageIndex: 0, pageSize: 25 } },
  });

  const selectionCount = table.getSelectedRowModel().rows.length;

  // menu actions
  const closeMenu = () => {
    setMenuAnchorEl(null);
    setMenuTruck(null);
  };

  const openEdit = (t: Truck) => {
    setEditingTruck(t);
    setIsBulkEdit(false);
    setModalOpen(true);
    closeMenu();
  };

  const openDeleteConfirm = (t: Truck) => {
    setDeletingTruck(t);
    setDeleteConfirmOpen(true);
    closeMenu();
  };

  const handleDeleteConfirm = async () => {
    if (!deletingTruck || !deletingTruck.licensePlate) return;
    await deleteTruck(deletingTruck.licensePlate);
    setDeleteConfirmOpen(false);
    setDeletingTruck(null);
  };

  const openEditSelected = () => {
    const selected = table.getSelectedRowModel().rows.map((r) => r.original as Truck);
    if (!selected.length) return;
    setIsBulkEdit(true);
    setEditingTruck(null);
    setModalOpen(true);
  };

  const handleModalSave = async (payload: FormTruck) => {
    if (isBulkEdit) {
      const selectedIds = table.getSelectedRowModel().rows.map((r) => (r.original as Truck).licensePlate).filter(Boolean) as string[];
      if (!selectedIds.length) return;
      await Promise.all(selectedIds.map((id) => updateTruck(id, payload)));
      return;
    }
    if (editingTruck && editingTruck.licensePlate) {
      await updateTruck(editingTruck.licensePlate, payload);
    } else {
      await createTruck(payload);
    }
  };

  const handleBulkDeleteConfirm = async () => {
    const selectedIds = table.getSelectedRowModel().rows.map((r) => (r.original as Truck).licensePlate).filter(Boolean) as string[];
    if (!selectedIds.length) {
      setBulkDeleteOpen(false);
      return;
    }
    await Promise.all(selectedIds.map((id) => deleteTruck(id)));
    setBulkDeleteOpen(false);
  };

  return (
    <DashboardLayout>
      <Box sx={{ display: "flex", justifyContent: "center", p: 1 }}>
        <Box sx={{ width: { xs: "100%", lg: "100%" }, maxWidth: "1400px" }}>
          <Box
            sx={{
              flexDirection: "row",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography
              variant="h4"
              sx={{
                mb: 2,
                display: "flex",
                alignItems: "center",
                gap: 1,
                color: "primary.main",
              }}
            >
              <UserIcon sx={{ fontSize: "2.5rem", bottom: "2px", position: "relative" }} color="primary" />
              Manage Trucks
            </Typography>
            <Box
              sx={{
                display: "flex",
                gap: 1,
                opacity: selectionCount > 0 ? 1 : 0,
                transition: "opacity 0.3s ease",
              }}
            >
              <Button variant="outlined" color="primary" onClick={() => setBulkDeleteOpen(true)} startIcon={<DeleteIcon />}>
                Delete Selected
              </Button>
              <Button variant="outlined" color="primary" onClick={openEditSelected} startIcon={<EditIcon />}>
                Edit Selected
              </Button>
            </Box>
          </Box>

          <MaterialReactTable table={table} />
        </Box>
      </Box>

      <Menu
        id="row-actions-menu-truck"
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={closeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem
          onClick={() => {
            if (menuTruck) openEdit(menuTruck);
            else closeMenu();
          }}
        >
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit Truck</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menuTruck) openDeleteConfirm(menuTruck);
            else closeMenu();
          }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Delete Truck</ListItemText>
        </MenuItem>
      </Menu>

      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Truck</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete{" "}
            <strong>{deletingTruck ? deletingTruck.licensePlate ?? deletingTruck.id : ""}</strong> ?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDeleteConfirm}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={bulkDeleteOpen} onClose={() => setBulkDeleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Selected Trucks</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete the selected trucks? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDeleteOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleBulkDeleteConfirm}>
            Delete Selected
          </Button>
        </DialogActions>
      </Dialog>

      <TruckModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setIsBulkEdit(false);
          setEditingTruck(null);
        }}
        onSave={handleModalSave}
        truck={editingTruck ?? undefined}
        bulk={isBulkEdit}
      />

      <Fade in={false} timeout={300} unmountOnExit>
        <Box>
          <Fab
            aria-label="bulk-actions"
            onClick={() => {
              const ids = table.getSelectedRowModel().rows.map((r) => (r.original as Truck).licensePlate);
              console.log("Bulk action on selected:", ids);
            }}
            sx={{
              position: "fixed",
              bottom: 24,
              right: 24,
              zIndex: 1400,
              width: 100,
              height: 100,
              bgcolor: theme.brand,
              color: "#fff",
              "&:hover": { bgcolor: theme.brand },
              mb: 1,
            }}
          >
            <EditIcon sx={{ fontSize: 32 }} />
          </Fab>
        </Box>
      </Fade>
    </DashboardLayout>
  );
};

export default ManageTrucks;
